import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FaTiktok } from "react-icons/fa";
import { Download, Play, Shield, Loader } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { useUrlValidation } from "@/hooks/useUrlValidation";
interface InitialHeroStateProps {
  url: string;
  onUrlChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  isOnline: boolean;
}
const InitialHeroState = ({
  url,
  onUrlChange,
  onSubmit,
  isLoading,
  isOnline
}: InitialHeroStateProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();
  const {
    validateTikTokUrl
  } = useUrlValidation();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      toast({
        variant: "destructive",
        title: "Please enter a URL",
        description: "Enter a TikTok collection URL to continue."
      });
      return;
    }
    if (!validateTikTokUrl(url)) {
      toast({
        variant: "destructive",
        title: "Invalid URL format",
        description: "Please enter a valid TikTok collection URL."
      });
      return;
    }
    if (!isOnline) {
      toast({
        variant: "destructive",
        title: "No internet connection",
        description: "Please check your connection and try again."
      });
      return;
    }
    if (isLoading) return;

    // Blur input field to dismiss mobile keyboard
    if (isMobile) {
      (document.activeElement as HTMLElement)?.blur();
      // Add delay for mobile to ensure keyboard is dismissed
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Navigate to scan page with the URL
    navigate(`/scan?url=${encodeURIComponent(url)}`);
  };
  return <div className="relative overflow-hidden bg-gradient-to-b from-[#0F111A] to-[#151823] py-8 md:py-24">
      <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[600px] h-[600px] bg-[#00E5FF] rounded-full blur-[350px] opacity-20" />
      
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          <div className="text-left">
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 lg:mb-6 leading-tight">
              Download TikTok<br />
              <span className="text-[#00E5FF]">Collections in 1 Click</span>
            </h1>
            <p className="text-sm lg:text-lg text-gray-400 mb-6 lg:mb-8">TokCollection downloads all your videos from any TikTok Collections  No extensions, no code. Just paste the link and go!</p>

            <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
              <div className="bg-[#151820] rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-lg">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <FaTiktok className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
                  </div>
                  
                  <Input type="text" placeholder="Paste your TikTok collection link here" className="h-10 lg:h-12 pl-10 bg-[#1C1F26] border-gray-700 text-white placeholder:text-gray-500 rounded-lg lg:rounded-xl text-sm lg:text-base" value={url} onChange={e => onUrlChange(e.target.value)} disabled={isLoading} />
                </div>

                <Button type="submit" className="w-full mt-3 lg:mt-4 h-10 lg:h-12 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#151820] font-medium rounded-lg lg:rounded-xl text-sm lg:text-base relative" disabled={isLoading || !isOnline || !url.trim()}>
                  {isLoading ? <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </> : <>
                      <Download className="mr-2 h-4 w-4 lg:h-5 lg:w-5" />
                      Download Videos
                    </>}
                </Button>
              </div>

              <div className="flex items-center gap-2 text-xs lg:text-sm text-gray-400">
                <Shield className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
                <span>Your collection data is secure and temporarily stored for 72 hours</span>
              </div>
            </form>
          </div>

          {!isMobile && <div className="hidden lg:block">
              <Card className="relative overflow-hidden bg-gradient-to-br from-[#1A2942] to-[#1E1E2E] border-none rounded-3xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <FaTiktok className="w-8 h-8 text-white" />
                    <h3 className="text-2xl font-semibold text-white">My Cooking Collection</h3>
                  </div>
                  <div className="px-4 py-1.5 bg-white/10 rounded-full">
                    <span className="text-sm text-white">24 total videos</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative rounded-xl overflow-hidden aspect-video bg-black/20">
                    <img src="/lovable-uploads/cfba2c5c-2feb-4fce-8b76-7ac9e83e8c0b.png" alt="Creator with neon lights" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                  </div>

                  <div className="relative rounded-xl overflow-hidden aspect-video bg-black/20">
                    <img src="/lovable-uploads/97c7eb6e-9b6e-4fa9-888a-64c16ce04a56.png" alt="Content creator setup" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                  </div>

                  <div className="relative rounded-xl overflow-hidden aspect-video bg-black/20">
                    <img src="/lovable-uploads/4b937c67-5b5d-482f-866a-a93d4911ce80.png" alt="Chef cooking tutorial" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                  </div>

                  <div className="relative rounded-xl overflow-hidden aspect-video bg-black/20">
                    <img src="/lovable-uploads/d0391884-a2be-4d64-b016-7ad6e2b79050.png" alt="Makeup tutorial creator" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white fill-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>}
        </div>
      </div>
    </div>;
};
export default InitialHeroState;